/**
 * Staff — Trainee profile.
 *
 * URL: /staff/trainees/:email
 *
 * Shows a single trainee's Ox Resin account (name, phone, company, brand
 * access, joined date) alongside their full training-course booking history.
 * Staff can:
 *   - See at a glance which brands they've been through
 *   - Re-send the welcome (joining + set-password) emails for any booking
 *   - Jump to any course they've been on
 *   - See outstanding balances across every course they've been booked on
 *
 * This is the page linked from the "duplicate email" confirmation on the
 * Add Trainee dialog and — going forward — from anywhere else we surface
 * an installer/trainee reference.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Home, Mail, Phone, Building2, ShieldCheck, User as UserIcon,
  CalendarDays, RefreshCcw, ExternalLink, PoundSterling, CheckCircle2,
  Users, FileText, ShoppingCart, Truck, Hash, CalendarPlus,
} from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';

const BRAND_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const BRAND_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D5AB09' };

const STATUS_PILL = {
  draft:      'bg-gray-200 text-gray-700',
  submitted:  'bg-blue-100 text-blue-700',
  pending:    'bg-blue-100 text-blue-700',
  invoiced:   'bg-amber-100 text-amber-800',
  paid:       'bg-emerald-100 text-emerald-700',
  dispatched: 'bg-purple-100 text-purple-700',
  delivered:  'bg-green-200 text-green-800',
  cancelled:  'bg-red-100 text-red-700',
};

const fmtDT = (s) => s
  ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

function Pill({ status }) {
  if (!status) return null;
  return (
    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full ${STATUS_PILL[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function ActivitySection({ title, icon: Icon, rows, empty, render, testid }) {
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500 ml-auto">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic p-4">{empty}</p>
      ) : (
        <div className="divide-y">{rows.map(render)}</div>
      )}
    </Card>
  );
}

export default function TraineeProfile() {
  const navigate = useNavigate();
  const { email: rawEmail } = useParams();
  const email = decodeURIComponent(rawEmail || '').toLowerCase();
  const { user: me } = useContext(AuthContext);

  const [data, setData] = useState(null);   // full lookup response
  const [snapshot, setSnapshot] = useState(null); // installer activity (leads/quotes/orders)
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    course_id: '',
    payment_total: '',
    payment_received: '',
    send_welcome: true,
  });

  const fetchProfile = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const [lookupRes, snapshotRes, coursesRes] = await Promise.all([
        fetch(
          `${API_URL}/api/staff/trainees/lookup?email=${encodeURIComponent(email)}`,
          { credentials: 'include' },
        ),
        fetch(
          `${API_URL}/api/staff/installer-snapshot/${encodeURIComponent(email)}`,
          { credentials: 'include' },
        ),
        fetch(
          `${API_URL}/api/staff/training-courses`,
          { credentials: 'include' },
        ),
      ]);
      if (!lookupRes.ok) {
        const err = await lookupRes.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to load trainee');
      }
      setData(await lookupRes.json());
      // Snapshot 404s are expected when the trainee doesn't yet have any activity
      // (or the email isn't linked to a user_id) — swallow silently.
      setSnapshot(snapshotRes.ok ? await snapshotRes.json() : null);
      setAvailableCourses(coursesRes.ok ? await coursesRes.json() : []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    if (!me) return;
    if (!['admin', 'manager', 'team'].includes(me.role)) {
      navigate('/', { replace: true });
      return;
    }
    fetchProfile();
  }, [me, fetchProfile, navigate]);

  const resendWelcome = async (booking) => {
    if (!booking?.course?.id || !booking?.id) return;
    setResendingId(booking.id);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${booking.course.id}/registrations/${booking.id}/send-welcome`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to re-send welcome emails');
      }
      toast.success(`Welcome emails re-sent to ${email}`);
      fetchProfile();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResendingId(null);
    }
  };

  const openBooking = () => {
    // Pre-select the first course the trainee ISN'T on yet.
    const bookedIds = new Set((data?.bookings || []).map(b => b.course?.id).filter(Boolean));
    const next = availableCourses.find(c => !bookedIds.has(c.id));
    setBookingForm({
      course_id: next?.id || '',
      payment_total: '',
      payment_received: '',
      send_welcome: true,
    });
    setBookingOpen(true);
  };

  const submitBooking = async () => {
    if (!bookingForm.course_id) {
      toast.error('Pick a course first.');
      return;
    }
    setBookingBusy(true);
    try {
      const payload = {
        name: data?.user?.name || email,
        email,
        phone: data?.user?.phone || null,
        send_welcome: bookingForm.send_welcome,
      };
      if (bookingForm.payment_total !== '') payload.payment_total = Number(bookingForm.payment_total);
      if (bookingForm.payment_received !== '') payload.payment_received = Number(bookingForm.payment_received);
      const res = await fetch(
        `${API_URL}/api/staff/training-courses/${bookingForm.course_id}/registrations`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to book onto course');
      }
      const course = availableCourses.find(c => c.id === bookingForm.course_id);
      toast.success(`${payload.name} booked onto ${course?.name || 'course'}`);
      setBookingOpen(false);
      fetchProfile();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBookingBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading…</div>;
  }

  if (!data || !data.exists) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="border-b bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Link to="/" className="text-gray-500 hover:text-gray-900"><Home className="w-5 h-5" /></Link>
            <h1 className="font-bold text-gray-900">Trainee profile</h1>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-8 text-center text-gray-500">
          No Ox Resin account exists for <strong className="text-gray-700">{email}</strong> yet.
        </div>
      </div>
    );
  }

  const { user, bookings = [], bookings_count = 0 } = data;
  const activeBrands = Object.entries(user.access || {}).filter(([_, v]) => v).map(([k]) => k);
  const totalOwed = bookings.reduce((s, b) => s + (b.balance_paid_at ? 0 : (Number(b.balance_owed) || 0)), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b sticky top-0 z-20 bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900" data-testid="trainee-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="text-gray-500 hover:text-gray-900"><Home className="w-5 h-5" /></Link>
          <UserIcon className="w-5 h-5 text-gray-500" />
          <h1 className="font-bold text-gray-900 truncate">{user.name || email}</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Account card */}
        <Card className="p-5" data-testid="trainee-account-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600">
              {(user.name || email).slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">{user.name || '—'}</h2>
              <div className="text-xs text-gray-500 flex items-center gap-1 truncate">
                <Mail className="w-3 h-3" /> {user.email}
              </div>
            </div>
            {user.has_password ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold whitespace-nowrap inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Password set
              </span>
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold whitespace-nowrap">
                Awaiting password
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            {user.company_name && (
              <div>
                <div className="text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company</div>
                <div className="font-semibold text-gray-800 mt-0.5">{user.company_name}</div>
              </div>
            )}
            {user.phone && (
              <div>
                <div className="text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</div>
                <div className="font-semibold text-gray-800 mt-0.5">{user.phone}</div>
              </div>
            )}
            <div>
              <div className="text-gray-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Joined</div>
              <div className="font-semibold text-gray-800 mt-0.5">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </div>
            </div>
            <div>
              <div className="text-gray-500">Role</div>
              <div className="font-semibold text-gray-800 mt-0.5">{user.role || 'installer'}</div>
            </div>
          </div>
          {activeBrands.length > 0 && (
            <div className="mt-4 pt-3 border-t flex flex-wrap gap-2">
              {activeBrands.map((b) => (
                <span
                  key={b}
                  className="text-[11px] font-bold text-white px-2 py-0.5 rounded"
                  style={{ background: BRAND_COLOR[b] || '#111827' }}
                >
                  {BRAND_LABEL[b] || b}
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Bookings */}
        <Card className="overflow-hidden" data-testid="trainee-bookings-card">
          <div className="p-4 border-b flex items-center gap-2 flex-wrap">
            <CalendarDays className="w-4 h-4 text-gray-400 shrink-0" />
            <h2 className="font-semibold text-gray-900">Training bookings</h2>
            <span className="text-xs text-gray-500">
              {bookings_count} course{bookings_count === 1 ? '' : 's'}
              {totalOwed > 0 && (
                <> · <span className="text-rose-700 font-semibold">£{totalOwed.toFixed(2)} outstanding</span></>
              )}
            </span>
            <Button
              size="sm"
              onClick={openBooking}
              disabled={availableCourses.length === 0}
              data-testid="trainee-book-onto-course-btn"
              className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              title={availableCourses.length === 0 ? 'No courses to book onto — create one first' : 'Book this trainee onto another course'}
            >
              <CalendarPlus className="w-3.5 h-3.5 mr-1" />
              Book onto course
            </Button>
          </div>
          {bookings.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="trainee-bookings-empty">
              No course bookings yet.
            </div>
          ) : (
            <div className="divide-y">
              {bookings.map((b) => {
                const brand = (b.course?.sections || [])[0];
                return (
                  <div key={b.id} className="p-4 flex items-start gap-3" data-testid={`booking-${b.id}`}>
                    <div
                      className="w-1 self-stretch rounded"
                      style={{ background: BRAND_COLOR[brand] || '#111827' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">
                        {b.course?.name || 'Course'}
                        <span className="ml-2 font-mono text-xs text-gray-500">{b.course?.reference}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {b.course?.date || '—'}
                        {b.confirmed_at && <> · <span className="text-emerald-700">Attended {new Date(b.confirmed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span></>}
                        {b.welcome_sent_at && <> · <span className="text-gray-600">Welcome sent</span></>}
                      </div>
                      <div className="text-xs mt-1 flex flex-wrap gap-3">
                        {b.payment_total ? (
                          <span className="text-gray-700 inline-flex items-center gap-1">
                            <PoundSterling className="w-3 h-3" />
                            Fee £{Number(b.payment_total).toFixed(2)} · Paid £{Number(b.payment_received || 0).toFixed(2)}
                          </span>
                        ) : null}
                        {b.balance_paid_at ? (
                          <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Balance cleared
                          </span>
                        ) : Number(b.balance_owed) > 0 ? (
                          <span className="text-rose-700 font-semibold">£{Number(b.balance_owed).toFixed(2)} owed</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resendWelcome(b)}
                        disabled={resendingId === b.id}
                        data-testid={`resend-welcome-${b.id}`}
                        className="text-xs"
                      >
                        <RefreshCcw className={`w-3 h-3 mr-1 ${resendingId === b.id ? 'animate-spin' : ''}`} />
                        {resendingId === b.id ? 'Sending…' : (b.welcome_sent_at ? 'Re-send' : 'Send welcome')}
                      </Button>
                      <Link
                        to={`/staff/training-courses/${b.course?.id}`}
                        className="text-[11px] text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 justify-center"
                        data-testid={`view-course-${b.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> Course
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Activity — leads, quotes, orders linked to this trainee's user_id. */}
        {snapshot && (
          <>
            <div className="flex items-center gap-2 pt-2">
              <Hash className="w-4 h-4 text-gray-400" />
              <h2 className="font-semibold text-gray-900">Portal activity</h2>
              {snapshot.user?.account_code && (
                <span className="ml-1 text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  {snapshot.user.account_code}
                </span>
              )}
              <Link
                to={`/staff/installer/${encodeURIComponent(snapshot.user?.account_code || email)}`}
                className="ml-auto text-[11px] text-[#006400] hover:underline inline-flex items-center gap-1"
                data-testid="trainee-open-full-snapshot"
              >
                Open full snapshot <ExternalLink className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ActivitySection
                title="Leads"
                icon={Users}
                testid="trainee-activity-leads"
                rows={snapshot.leads || []}
                empty="No leads on this account yet."
                render={(l) => (
                  <div key={l.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{l.customer_name || '—'}</div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {l.service_type || 'lead'}{l.customer_phone ? ` · ${l.customer_phone}` : ''}
                      </div>
                    </div>
                    <Pill status={l.status} />
                  </div>
                )}
              />

              <ActivitySection
                title="Project Quotes"
                icon={FileText}
                testid="trainee-activity-project-quotes"
                rows={snapshot.project_quotes || []}
                empty="No customer-facing project quotes."
                render={(q) => (
                  <div key={q.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono font-bold text-gray-900 truncate">
                        {q.reference || q.id?.slice(0, 8)}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {q.customer_name || '—'} · {fmtDT(q.created_at)}
                      </div>
                    </div>
                    <Pill status={q.status} />
                  </div>
                )}
              />

              <ActivitySection
                title="ProBound Material Quotes"
                icon={ShoppingCart}
                testid="trainee-activity-probound"
                rows={snapshot.probound_quotes || []}
                empty="No material quotes."
                render={(q) => (
                  <div key={q.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono font-bold text-gray-900 truncate">
                        {q.reference || q.id?.slice(0, 8)}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        {q.customer_details?.name || '—'} · {fmtDT(q.created_at)}
                      </div>
                    </div>
                    <Pill status={q.status} />
                  </div>
                )}
              />

              <ActivitySection
                title="ProDecor Orders"
                icon={Truck}
                testid="trainee-activity-prodecor"
                rows={snapshot.prodecor_orders || []}
                empty="No ProDecor orders."
                render={(o) => (
                  <div key={o.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono font-bold text-gray-900 truncate">
                        {o.reference || o.id?.slice(0, 8)}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        £{Number(o.total || 0).toFixed(2)} · {fmtDT(o.created_at)}
                      </div>
                    </div>
                    <Pill status={o.status} />
                  </div>
                )}
              />

              <ActivitySection
                title="ProEpoxy Orders"
                icon={Truck}
                testid="trainee-activity-proepoxy"
                rows={snapshot.proepoxy_orders || []}
                empty="No ProEpoxy orders."
                render={(o) => (
                  <div key={o.id} className="px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono font-bold text-gray-900 truncate">
                        {o.reference || o.id?.slice(0, 8)}
                      </div>
                      <div className="text-[11px] text-gray-500 truncate">
                        £{Number(o.total || 0).toFixed(2)} · {fmtDT(o.created_at)}
                      </div>
                    </div>
                    <Pill status={o.status} />
                  </div>
                )}
              />
            </div>
          </>
        )}
      </div>

      <Dialog open={bookingOpen} onOpenChange={(o) => { if (!bookingBusy) setBookingOpen(o); }}>
        <DialogContent data-testid="trainee-book-onto-course-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Book onto another course</DialogTitle>
            <DialogDescription>
              Adds <strong>{data?.user?.name || email}</strong> to another training course.
              Their existing Ox Resin account is reused — the course&apos;s brand access is unioned onto whatever they already have.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="book-course">Course</Label>
              {(() => {
                const bookedIds = new Set((data?.bookings || []).map(b => b.course?.id).filter(Boolean));
                const options = availableCourses.filter(c => !bookedIds.has(c.id));
                if (options.length === 0) {
                  return (
                    <p className="mt-1 text-sm text-gray-500 italic" data-testid="book-onto-course-no-options">
                      No courses to book onto — {availableCourses.length === 0 ? 'create a course first.' : 'this trainee is already on every course.'}
                    </p>
                  );
                }
                return (
                  <select
                    id="book-course"
                    data-testid="book-onto-course-select"
                    value={bookingForm.course_id}
                    onChange={(e) => setBookingForm(f => ({ ...f, course_id: e.target.value }))}
                    className="mt-1 w-full border rounded-lg p-2.5 bg-white text-sm"
                  >
                    {options.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.date} ({c.reference || c.id.slice(0, 8)})
                      </option>
                    ))}
                  </select>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="book-fee">Course fee</Label>
                <Input
                  id="book-fee"
                  type="number"
                  min={0}
                  step="0.01"
                  value={bookingForm.payment_total}
                  onChange={(e) => setBookingForm(f => ({ ...f, payment_total: e.target.value }))}
                  data-testid="book-onto-course-fee"
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="book-paid">Paid so far</Label>
                <Input
                  id="book-paid"
                  type="number"
                  min={0}
                  step="0.01"
                  value={bookingForm.payment_received}
                  onChange={(e) => setBookingForm(f => ({ ...f, payment_received: e.target.value }))}
                  data-testid="book-onto-course-paid"
                  placeholder="Optional"
                  className="mt-1"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <Checkbox
                checked={bookingForm.send_welcome}
                onCheckedChange={(v) => setBookingForm(f => ({ ...f, send_welcome: !!v }))}
                data-testid="book-onto-course-send-welcome"
              />
              Send joining instructions email now
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingOpen(false)} disabled={bookingBusy}>Cancel</Button>
            <Button
              onClick={submitBooking}
              disabled={bookingBusy || !bookingForm.course_id}
              data-testid="book-onto-course-submit"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CalendarPlus className="w-4 h-4 mr-1.5" />
              {bookingBusy ? 'Booking…' : 'Add to course'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
